/**
 * @file ConversationMenu.tsx
 * @description Меню «⋯» диалога (chat-window-design.md §1д, §4): пункты —
 * из реестра `chat-actions.ts` по признакам `actions` карточки. На широком
 * экране — выпадающее меню, на телефоне — нижняя шторка с пунктами 48 и
 * [Отмена].
 */

import { MoreHorizontal } from "lucide-react";
import { Fragment, useState } from "react";

import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import type { ChatActionKey, ChatActionSpec } from "./chat-actions";
import { CHAT_TEXT } from "./chat-texts";

interface Props {
  items: ChatActionSpec[];
  phone: boolean;
  /** Подпись шторки: «Евгений Кривошеев · Z-…06». */
  caption: string;
  onRun: (key: ChatActionKey) => void;
}

/** Разделитель между группами пунктов. */
function separated(items: ChatActionSpec[]) {
  return items.map((item, i) => ({ item, separator: i > 0 && items[i - 1]?.group !== item.group }));
}

export function ConversationMenu({ items, phone, caption, onRun }: Props) {
  const [sheetOpen, setSheetOpen] = useState(false);
  if (items.length === 0) return null;

  if (phone) {
    return (
      <>
        <Button variant="ghost" size="icon" className="size-12" aria-label={CHAT_TEXT.menuLabel} onClick={() => setSheetOpen(true)}>
          <MoreHorizontal className="size-5" aria-hidden />
        </Button>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="bottom" showClose={false} className="gap-1 rounded-t-2xl p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            <SheetHeader className="px-2 pb-1">
              <SheetTitle className="sr-only">{CHAT_TEXT.menuLabel}</SheetTitle>
              <SheetDescription className="truncate">{caption}</SheetDescription>
            </SheetHeader>
            {separated(items).map(({ item, separator }) => (
              <Fragment key={item.key}>
                {separator ? <div className="my-1 h-px bg-outline-variant" aria-hidden /> : null}
                <Button
                  variant="ghost"
                  className="min-h-12 w-full justify-start"
                  onClick={() => {
                    setSheetOpen(false);
                    onRun(item.key);
                  }}
                >
                  <item.icon className="size-5" aria-hidden />
                  {item.label}
                </Button>
              </Fragment>
            ))}
            <Button variant="outline" className="mt-2 min-h-12 w-full" onClick={() => setSheetOpen(false)}>
              {CHAT_TEXT.cancel}
            </Button>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={(p) => (
          <Button {...p} variant="ghost" size="icon" className="size-12 lg:size-8" aria-label={CHAT_TEXT.menuLabel}>
            <MoreHorizontal className="size-4" aria-hidden />
          </Button>
        )}
      />
      <DropdownMenuContent align="end" sideOffset={6} className="min-w-56">
        {separated(items).map(({ item, separator }) => (
          <Fragment key={item.key}>
            {separator ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem className="py-2" onClick={() => onRun(item.key)}>
              <item.icon className="size-4" aria-hidden />
              {item.label}
            </DropdownMenuItem>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
